import { useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { CLIENT_SITES } from "../data/infoPages";

type Item = {
  name: string;
  trade: string;
  url: string;
  image?: string;
  detail: string;
  // where "build me one like this" goes
  likeHref: string;
};

const clients: Item[] = CLIENT_SITES.map((site) => ({
  ...site,
  // Carried to the contact form by the "Build me one like this" link.
  likeHref: `/contact.html?like=${(site.image ?? "").replace(/^\/work\/|\.webp$/g, "")}`,
}));

// Our own products. Screenshots are each app's public landing page.
const apps: Item[] = [
  {
    name: "Skeulur",
    trade: "Employee scheduling app for restaurants",
    url: "https://skeulur.rare-works.com",
    image: "/work/skeulur.webp",
    detail:
      "Work schedules, time-off requests, and shift swaps, built for the way restaurants run. It installs on a phone like an app, and Stacy's Skillet runs its staff on it.",
    likeHref: "/custom-builds.html",
  },
];

const GROUPS = [
  {
    key: "clients",
    label: "Clients we've built for",
    meta: `${clients.length} live websites in South Mississippi`,
    items: clients,
    aria: "Preview a client website",
    open: "Visit the live site",
    like: "Build me one like this",
    note: "Real businesses. Every site is live.",
    more: { href: "/our-work.html", label: "See all our work" },
  },
  {
    key: "apps",
    label: "Apps we've built",
    meta: `${apps.length} live ${apps.length === 1 ? "app, our own product" : "apps, our own products"}`,
    items: apps,
    aria: "Preview an app",
    open: "Open the app",
    like: "Have us build yours",
    note: "Designed, coded, and run by us.",
    more: { href: "/custom-builds.html", label: "About custom builds" },
  },
];

// embedded: rendered inside the Hero, which already supplies the page gutters.
const Clients = ({ embedded = false }: { embedded?: boolean }) => {
  const reduceMotion = useReducedMotion();
  const [groupIndex, setGroupIndex] = useState(0);
  const [active, setActive] = useState(0);
  const tabs = useRef<(HTMLButtonElement | null)[]>([]);
  const group = GROUPS[groupIndex];
  const items = group.items;
  const item = items[active] ?? items[0];

  const pickGroup = (index: number) => {
    if (index === groupIndex) return;
    setGroupIndex(index);
    setActive(0);
  };

  const onKeyDown = (e: React.KeyboardEvent, index: number) => {
    let next: number | undefined;
    if (e.key === "ArrowRight" || e.key === "ArrowDown")
      next = (index + 1) % items.length;
    if (e.key === "ArrowLeft" || e.key === "ArrowUp")
      next = (index - 1 + items.length) % items.length;
    if (e.key === "Home") next = 0;
    if (e.key === "End") next = items.length - 1;
    if (next === undefined) return;
    e.preventDefault();
    setActive(next);
    tabs.current[next]?.focus();
  };

  return (
    <section
      id="work"
      aria-label="Our work"
      className={`relative ${embedded ? "mb-16" : "pt-8 pb-20 overflow-hidden"}`}
    >
      <div className={embedded ? "" : "max-w-6xl mx-auto px-4 sm:px-6 lg:px-8"}>
        <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 border-t border-white/10 pt-5 pb-4">
          <div role="group" aria-label="What to show" className="flex items-center gap-x-4 sm:gap-x-6">
            {GROUPS.map((g, index) => (
              <h2 key={g.key} className="text-sm sm:text-lg font-medium">
                <button
                  type="button"
                  aria-pressed={index === groupIndex}
                  onClick={() => pickGroup(index)}
                  className={`pb-0.5 border-b transition-colors outline-hidden focus-visible:ring-2 focus-visible:ring-teal-500/60 rounded-xs ${
                    index === groupIndex
                      ? "text-white border-teal-400"
                      : "text-gray-400 border-transparent hover:text-gray-200"
                  }`}
                >
                  {g.label}
                </button>
              </h2>
            ))}
          </div>
          <span className="text-sm text-gray-400 sm:text-right">{group.meta}</span>
        </div>

        <div
          role="group"
          aria-label={group.aria}
          className="grid grid-cols-2 gap-x-6 sm:flex sm:gap-8 border-b border-white/10 mb-6"
        >
          {items.map((c, index) => (
            <button
              key={c.name}
              ref={(el) => (tabs.current[index] = el)}
              type="button"
              aria-pressed={index === active}
              onClick={() => setActive(index)}
              onKeyDown={(e) => onKeyDown(e, index)}
              className={`text-left text-sm sm:text-base py-3 border-b-2 -mb-px transition-colors outline-hidden focus-visible:ring-2 focus-visible:ring-teal-500/60 rounded-xs ${
                index === active
                  ? "text-white font-medium border-teal-400"
                  : "text-gray-400 border-transparent hover:text-gray-200"
              }`}
            >
              {c.name}
            </button>
          ))}
        </div>

        <div className="grid lg:grid-cols-[minmax(0,1fr)_300px] gap-6 lg:gap-10">
          <a
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`${group.open}: ${item.name}`}
            className="group relative block rounded-2xl glass p-2 sm:p-3 overflow-hidden hover:border-teal-500/50 transition-colors"
          >
            <div className="overflow-hidden rounded-xl">
              <motion.img
                key={item.name}
                src={item.image}
                alt={`${item.name} homepage`}
                width={1024}
                height={640}
                initial={reduceMotion ? false : { opacity: 0.2 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                className="w-full aspect-16/10 object-cover object-top"
              />
            </div>
          </a>

          <div className="flex flex-col items-start" aria-live="polite">
            <p className="text-sm text-gray-400">{item.trade}</p>
            <h3 className="text-3xl sm:text-4xl font-semibold tracking-tight text-white mt-4 lg:mt-8 mb-4">
              {item.name}
            </h3>
            <p className="text-sm text-gray-400 leading-relaxed mb-6">{item.detail}</p>
            <a
              href={item.likeHref}
              className="flex w-full items-center justify-between gap-4 rounded-xl px-5 py-3.5 text-sm font-medium text-white bg-linear-to-r/srgb from-teal-500 to-cyan-600 hover:brightness-110 transition-[filter]"
            >
              {group.like}
              <ArrowUpRight className="w-4 h-4" />
            </a>
            <a
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 mt-5 pb-1 text-sm text-gray-300 hover:text-white border-b border-gray-600 hover:border-white transition-colors"
            >
              {group.open}
              <ArrowUpRight className="w-4 h-4" />
            </a>
            <p className="text-sm text-gray-400 mt-5">
              {group.note}{" "}
              <a href={group.more.href} className="underline underline-offset-2 hover:text-gray-200">
                {group.more.label}
              </a>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Clients;
